// src/components/DeviceRegistrationForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Download, Check, Eye, EyeOff, HardDriveDownload, Loader2, Info, Circle } from 'lucide-react';
import { AddDeviceResponse } from '../types/devices';
import { Location } from '../types/locations';
import { devicesApi } from '../services/devicesApi';
import { locationsApi } from '../services/locationsApi';
import { useAuth } from '../contexts/AuthContext';

interface DeviceRegistrationFormProps {
    isOpen: boolean;
    onClose: () => void;
    onRegistered: () => void;  // Simplified - no need to pass response
}

// Where "Write All Files to SD Card" has got to - see writeAllToSdCard.
type CardFileState = 'waiting' | 'writing' | 'replaced' | 'new';
type CardFile = { name: string; state: CardFileState };
type SdCardWrite =
    | { phase: 'idle' }
    | { phase: 'working'; label: string; files: CardFile[] }
    | { phase: 'written'; place: string; files: CardFile[] }
    | { phase: 'problem'; tone: 'warning' | 'error'; title: string; detail: string };

// A file from the API for the card, or why it could not be had.
type CardDownload = { name: string; data: Blob } | { name: string; problem: string };

const fileExists = async (folder: FileSystemDirectoryHandle, name: string) => {
    try {
        await folder.getFileHandle(name);
        return true;
    } catch {
        return false;
    }
};

const DeviceRegistrationForm: React.FC<DeviceRegistrationFormProps> = ({
    isOpen,
    onClose,
    onRegistered
}) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        deviceName: '',
        locationId: undefined as number | undefined,
        username: 'pi',
        password: '',
        enableSsh: true,
        timezone: 'Europe/London',
        keyboardLayout: 'gb',
        wifiSsid: '',
        wifiPassword: '',
        wifiCountry: 'GB'
    });

    const [locations, setLocations] = useState<Location[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [registering, setRegistering] = useState(false);
    const [deviceCreated, setDeviceCreated] = useState(false);
    const [deviceResponse, setDeviceResponse] = useState<AddDeviceResponse | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showWifiPassword, setShowWifiPassword] = useState(false);
    const [sdCardWrite, setSdCardWrite] = useState<SdCardWrite>({ phase: 'idle' });
    // Bumped when the dialog closes, so a write still finishing for the last
    // device cannot report itself against the next one.
    const sdCardWriteRun = useRef(0);

    useEffect(() => {
        fetchLocations();
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setFormData({
                deviceName: '',
                locationId: undefined,
                username: 'pi',
                password: '',
                enableSsh: true,
                timezone: 'Europe/London',
                keyboardLayout: 'gb',
                wifiSsid: '',
                wifiPassword: '',
                wifiCountry: 'GB'
            });
            setDeviceCreated(false);
            setDeviceResponse(null);
            setError(null);
            setShowPassword(false);      // ADD THIS
            setShowWifiPassword(false);  // ADD THIS
            sdCardWriteRun.current++;
            setSdCardWrite({ phase: 'idle' });
        }
    }, [isOpen]);

    const fetchLocations = async () => {
        try {
            const data = await locationsApi.getAllLocations();
            setLocations(data);
        } catch (err) {
            console.error('Error fetching locations:', err);
            setError('Failed to load locations');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.deviceName.trim()) {
            setError('Device name is required');
            return;
        }

        try {
            setRegistering(true);
            setError(null);
            
            // Use the new addDevice endpoint
            const response = await devicesApi.addDevice(formData);
            
            setDeviceResponse(response);
            setDeviceCreated(true);
            // Don't call onRegistered here - wait for user to click Done
        } catch (err) {
            setError('Failed to create device: ' + (err instanceof Error ? err.message : String(err)));
        } finally {
            setRegistering(false);
        }
    };

    // DeviceRegistrationForm.tsx - Updated downloadConfigJson function
    // Replace the downloadConfigJson function in DeviceRegistrationForm.tsx with this:

    // Downloads an empty file named `ssh` for the boot partition.
    //
    // Raspberry Pi OS ships sshswitch.service, which looks for /boot/firmware/ssh
    // at boot, enables the SSH daemon and then deletes the file. So an empty file
    // is the whole mechanism - there is nothing to fetch from the API.
    //
    // Optional on purpose: it is per-card, chosen when the card is built, and
    // self-deleting. A customer device simply never gets one.
    //
    // If the browser insists on saving it as "ssh.txt", that still works -
    // sshswitch accepts either name.
    const downloadSshFile = () => {
        const blob = new Blob([''], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ssh';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // firstrun.json: which API the Pi registers with, and as which device.
    // Shared by its own download button and by writeAllToSdCard.
    const buildFirstrunJson = (): string | null => {
        if (!deviceResponse || !user) return null;

        const currentUrl = window.location.origin;       
        let apiEndpoint = 'https://api.pinage.co.uk'; // Default to production
        let environment = 'production'; // Add environment variable

        if (currentUrl.includes('localhost')) {
            // Development: Use local development server
            apiEndpoint = `http://local.pinage.co.uk:5110`;
            environment = 'development';
            console.log(`Development detected - using API endpoint: ${apiEndpoint}`);
        } else if (currentUrl.includes('betaapi.pinage.co.uk') || currentUrl.includes('betadashboard.pinage.co.uk')) {
            // Beta environment: Use beta API
            apiEndpoint = 'https://betaapi.pinage.co.uk';
            environment = 'beta';
            console.log(`Beta environment detected - using API endpoint: ${apiEndpoint}`);
        } else {
            // Production or any other domain: Use production API
            apiEndpoint = 'https://api.pinage.co.uk';
            environment = 'production';
            console.log(`Production environment - using API endpoint: ${apiEndpoint}`);
        }

        //console.log(`Environment detection: ${isDevelopment ? 'Development' : 'Production'}`);
        console.log(`Using API endpoint: ${apiEndpoint}`);

        const config = {
            environment: environment,  // Add this field
            api_endpoint: apiEndpoint,
            api_key: 'pinage_system_2024_k7m8n9p0q1r2s3t4u5v6w7x8y9z0',
            device_token: deviceResponse.deviceToken,
            device_id: deviceResponse.deviceId, 
            company_id: deviceResponse.companyId,
            device_name: deviceResponse.deviceName,
            jwt_token: deviceResponse.jwtToken  // New
        };

        return JSON.stringify(config, null, 2);
    };

    const downloadConfigJson = () => {
        const jsonString = buildFirstrunJson();
        if (!jsonString) return;

        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = 'firstrun.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadFirstrunScript = async () => {
        if (!deviceResponse) return;

        try {
             // Use the configured api instance instead of raw fetch
            const response = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceResponse.deviceId}/firstrun-script`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'X-API-Key': process.env.REACT_APP_API_KEY || ''
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'user-data';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else {
                alert('Error downloading firstrun script');
            }
        } catch (error) {
            console.error('Error downloading firstrun script:', error);
            alert('Error downloading firstrun script');
        }
    };

    const downloadFirstrunFiles = async () => {
    if (!deviceResponse) return;

    try {
        // Download user-data (cloud-init config - replaces firstrun.sh)
        const firstrunResponse = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceResponse.deviceId}/firstrun-script`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'X-API-Key': process.env.REACT_APP_API_KEY || ''
            }
        });

        // Download network-config (WiFi/Ethernet - Netplan format for Trixie)
        const networkConfigResponse = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceResponse.deviceId}/network-config`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'X-API-Key': process.env.REACT_APP_API_KEY || ''
            }
        });

        // Download meta-data (instance-id for cloud-init - CRITICAL for first boot detection)
        const metaDataResponse = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceResponse.deviceId}/meta-data`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'X-API-Key': process.env.REACT_APP_API_KEY || ''
            }
        });

        // Download firstrun-service.sh
        const serviceResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/system/firstrun-service`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'X-API-Key': process.env.REACT_APP_API_KEY || ''
            }
        });

        // Download firstrun-user-service.sh
        const userServiceResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/system/firstrun-user-service`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'X-API-Key': process.env.REACT_APP_API_KEY || ''
            }
        });

        if (firstrunResponse.ok && serviceResponse.ok && userServiceResponse.ok) {
            // Download user-data (cloud-init config)
            const firstrunBlob = await firstrunResponse.blob();
            const firstrunUrl = window.URL.createObjectURL(firstrunBlob);
            const firstrunLink = document.createElement('a');
            firstrunLink.href = firstrunUrl;
            firstrunLink.download = 'user-data';
            document.body.appendChild(firstrunLink);
            firstrunLink.click();
            document.body.removeChild(firstrunLink);
            window.URL.revokeObjectURL(firstrunUrl);

            // Download network-config (WiFi/Ethernet config for Trixie)
            if (networkConfigResponse.ok) {
                const networkConfigBlob = await networkConfigResponse.blob();
                const networkConfigUrl = window.URL.createObjectURL(networkConfigBlob);
                const networkConfigLink = document.createElement('a');
                networkConfigLink.href = networkConfigUrl;
                networkConfigLink.download = 'network-config';
                document.body.appendChild(networkConfigLink);
                networkConfigLink.click();
                document.body.removeChild(networkConfigLink);
                window.URL.revokeObjectURL(networkConfigUrl);
            }

            // Download meta-data (CRITICAL - tells cloud-init this is a new instance)
            if (metaDataResponse.ok) {
                const metaDataBlob = await metaDataResponse.blob();
                const metaDataUrl = window.URL.createObjectURL(metaDataBlob);
                const metaDataLink = document.createElement('a');
                metaDataLink.href = metaDataUrl;
                metaDataLink.download = 'meta-data';
                document.body.appendChild(metaDataLink);
                metaDataLink.click();
                document.body.removeChild(metaDataLink);
                window.URL.revokeObjectURL(metaDataUrl);
            }

            // Download firstrun-service.sh (unchanged)
            const serviceBlob = await serviceResponse.blob();
            const serviceUrl = window.URL.createObjectURL(serviceBlob);
            const serviceLink = document.createElement('a');
            serviceLink.href = serviceUrl;
            serviceLink.download = 'firstrun-service.sh';
            document.body.appendChild(serviceLink);
            serviceLink.click();
            document.body.removeChild(serviceLink);
            window.URL.revokeObjectURL(serviceUrl);

            // Download firstrun-user-service.sh (unchanged)
            const userServiceBlob = await userServiceResponse.blob();
            const userServiceUrl = window.URL.createObjectURL(userServiceBlob);
            const userServiceLink = document.createElement('a');
            userServiceLink.href = userServiceUrl;
            userServiceLink.download = 'firstrun-user-service.sh';
            document.body.appendChild(userServiceLink);
            userServiceLink.click();
            document.body.removeChild(userServiceLink);
            window.URL.revokeObjectURL(userServiceUrl);

        } else {
            if (!firstrunResponse.ok) {
                alert('Error downloading user-data');
            }
            if (!serviceResponse.ok) {
                alert('Error downloading firstrun-service.sh. Please ensure the PinagePi software package has been uploaded.');
            }
            if (!userServiceResponse.ok) {
                alert('Error downloading firstrun-user-service.sh. Please ensure the PinagePi software package has been uploaded.');
            }
        }
    } catch (error) {
        console.error('Error downloading cloud-init files:', error);
        alert('Error downloading cloud-init files');
    }
};
    
    const downloadFirstrunService = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/system/firstrun-service`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'X-API-Key': process.env.REACT_APP_API_KEY || ''
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'firstrun-service.sh';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else if (response.status === 404) {
                alert('firstrun-service.sh not available. Please ensure the PinagePi software package has been uploaded.');
            } else {
                alert('Error downloading firstrun-service.sh');
            }
        } catch (error) {
            console.error('Error downloading firstrun-service.sh:', error);
            alert('Error downloading firstrun-service.sh');
        }
    };

    const downloadSetupFiles = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/system/setup-files`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'X-API-Key': process.env.REACT_APP_API_KEY || ''
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'pinage-setup-files.zip';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } else if (response.status === 404) {
                alert('Setup files not available. Please ensure the PinagePi software package has been uploaded.');
            } else {
                alert('Error downloading setup files');
            }
        } catch (error) {
            console.error('Error downloading setup files:', error);
            alert('Error downloading setup files');
        }
    };

    // One of the card's files from the API. Resolves to the problem rather than
    // rejecting, because these run while the folder picker is open and nothing
    // is waiting on them yet.
    const fetchCardFile = async (name: string, path: string, fromPackage = false): Promise<CardDownload> => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'X-API-Key': process.env.REACT_APP_API_KEY || ''
                }
            });

            if (response.ok) {
                return { name, data: await response.blob() };
            }

            return {
                name,
                problem: fromPackage && response.status === 404
                    ? 'The PinagePi software package may not have been uploaded.'
                    : `The API answered HTTP ${response.status}.`
            };
        } catch {
            return { name, problem: "The API didn't answer." };
        }
    };

    // Writes everything the three buttons above download straight onto the SD
    // card, replacing whatever is already there. A newly flashed card can already
    // hold its own user-data, meta-data and network-config, and a download can
    // never replace them - the browser saves a second copy as "user-data (1)".
    //
    // Writing to a drive needs the File System Access API, which only Chrome and
    // Edge have: the user picks the card's boot partition, and the browser asks
    // whether this site may edit files there.
    const writeAllToSdCard = async () => {
        const firstrunJson = buildFirstrunJson();
        if (!deviceResponse || !firstrunJson || !window.showDirectoryPicker) return;

        const run = ++sdCardWriteRun.current;
        const update = (state: SdCardWrite) => {
            if (sdCardWriteRun.current === run) setSdCardWrite(state);
        };

        const id = deviceResponse.deviceId;
        const fromApi = [
            { name: 'user-data', path: `/devices/${id}/firstrun-script` },
            { name: 'network-config', path: `/devices/${id}/network-config` },
            { name: 'meta-data', path: `/devices/${id}/meta-data` },
            { name: 'firstrun-service.sh', path: '/api/system/firstrun-service', fromPackage: true },
            { name: 'firstrun-user-service.sh', path: '/api/system/firstrun-user-service', fromPackage: true },
            { name: 'pinage-setup-files.zip', path: '/api/system/setup-files', fromPackage: true }
        ];

        const files = ['firstrun.json', ...fromApi.map(f => f.name)]
            .map((name): CardFile => ({ name, state: 'waiting' }));
        const showProgress = (label: string) =>
            update({ phase: 'working', label, files: files.map(f => ({ ...f })) });

        // Start the downloads, but open the picker before waiting on anything. The
        // browser only shows it in direct response to the click, and time spent
        // awaiting first can use that up.
        const downloads = Promise.all(fromApi.map(f => fetchCardFile(f.name, f.path, f.fromPackage)));
        showProgress('Choosing the SD card...');

        let card: FileSystemDirectoryHandle;
        try {
            card = await window.showDirectoryPicker({ id: 'pinage-sd-card', mode: 'readwrite' });
        } catch (err) {
            // Closing the picker, or saying no to editing files, is not a failure.
            if (err instanceof DOMException && err.name === 'AbortError') {
                update({ phase: 'idle' });
            } else {
                update({ phase: 'problem', tone: 'error', title: "Couldn't open that folder", detail: String(err) });
            }
            return;
        }

        // Chrome can report a drive root's name as "\", or as nothing at all.
        const cardName = /^[\\/]*$/.test(card.name) ? '' : card.name;
        const place = cardName || 'the card';
        showProgress('Getting files from the API...');

        // Every Raspberry Pi OS boot partition has both of these. A folder without
        // them is not the card, and the files would only be left lying in it.
        if (!(await fileExists(card, 'config.txt')) || !(await fileExists(card, 'cmdline.txt'))) {
            update({
                phase: 'problem',
                tone: 'warning',
                title: `${cardName || 'That folder'} isn't an SD card's boot partition`,
                detail: "There's no config.txt or cmdline.txt on it, so nothing has been written. " +
                        'Press the button again and pick the drive called bootfs.'
            });
            return;
        }

        // All or nothing: the card is not touched until every file is in hand, so
        // a failed download leaves it exactly as it was.
        const contents: Blob[] = [new Blob([firstrunJson], { type: 'application/json' })];
        for (const download of await downloads) {
            if ('problem' in download) {
                update({
                    phase: 'problem',
                    tone: 'error',
                    title: `Couldn't get ${download.name} from the API`,
                    detail: `${download.problem} Nothing has been written, so ${place} is exactly as it was.`
                });
                return;
            }
            contents.push(download.data);
        }

        for (let i = 0; i < files.length; i++) {
            files[i].state = 'writing';
            showProgress(`Writing to ${place} — ${i + 1} of ${files.length}`);

            try {
                const existed = await fileExists(card, files[i].name);
                const handle = await card.getFileHandle(files[i].name, { create: true });
                // A new writable starts empty, and close() puts it in place of the
                // old file - that is what overwrites it.
                const writable = await handle.createWritable();
                await writable.write(contents[i]);
                await writable.close();
                files[i].state = existed ? 'replaced' : 'new';
            } catch (err) {
                update({
                    phase: 'problem',
                    tone: 'error',
                    title: `Couldn't write ${files[i].name} to ${place}`,
                    detail: `${err instanceof Error ? err.message : String(err)} ` +
                            (i === 0 ? 'Nothing has been written.' : 'The files before it were written.') +
                            ' Press the button to try again.'
                });
                return;
            }
        }

        update({ phase: 'written', place, files });
    };

    if (!isOpen) return null;

    const canWriteToCard = 'showDirectoryPicker' in window;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold">
                        {deviceCreated ? `Device "${deviceResponse?.deviceName}" Created Successfully` : 'Add New Device'}
                    </h2>
                    <button onClick={() => {
                        onClose();
                        if (deviceCreated) {
                            onRegistered(); // Only refresh if we created a device
                        }
                    }} className="text-gray-500 hover:text-gray-700">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {!deviceCreated ? (
                    <div className="max-h-[calc(90vh-120px)] overflow-y-auto">
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Device Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.deviceName}
                                    onChange={(e) => setFormData({ ...formData, deviceName: e.target.value })}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                    placeholder="e.g., Reception Display"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Location
                                </label>
                                <select
                                    value={formData.locationId || ''}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setFormData({
                                            ...formData,
                                            locationId: value ? Number(value) : undefined
                                        });
                                    }}
                                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                >
                                    <option value="">Select a location...</option>
                                    {locations.map(location => (
                                        <option key={location.locationId} value={location.locationId}>
                                            {location.locationName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* System Configuration Section */}
                            <div className="border-t pt-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">System Configuration</h3>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Username
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.username}
                                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                            placeholder="pi"
                                        />
                                    </div>

                                   <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Password
                                        </label>
                                        <div className="relative mt-1">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10"
                                                placeholder="Device password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5" />
                                                ) : (
                                                    <Eye className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={formData.enableSsh}
                                            onChange={(e) => setFormData({ ...formData, enableSsh: e.target.checked })}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Enable SSH for remote access</span>
                                    </label>
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Timezone
                                        </label>
                                        <select
                                            value={formData.timezone}
                                            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                        >
                                            <option value="Europe/London">Europe/London</option>
                                            <option value="America/New_York">America/New_York</option>
                                            <option value="America/Los_Angeles">America/Los_Angeles</option>
                                            <option value="Europe/Paris">Europe/Paris</option>
                                            <option value="Asia/Tokyo">Asia/Tokyo</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Keyboard Layout
                                        </label>
                                        <select
                                            value={formData.keyboardLayout}
                                            onChange={(e) => setFormData({ ...formData, keyboardLayout: e.target.value })}
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                        >
                                            <option value="gb">gb - UK English</option>
                                            <option value="us">us - US English</option>
                                            <option value="fr">fr - French</option>
                                            <option value="de">de - German</option>
                                            <option value="es">es - Spanish</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Network Configuration Section */}
                            <div className="border-t pt-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Network Configuration</h3>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        WiFi SSID
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.wifiSsid}
                                        onChange={(e) => setFormData({ ...formData, wifiSsid: e.target.value })}
                                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                        placeholder="OfficeWiFi"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            WiFi Password
                                        </label>
                                        <div className="relative mt-1">
                                            <input
                                                type={showWifiPassword ? "text" : "password"}
                                                value={formData.wifiPassword}
                                                onChange={(e) => setFormData({ ...formData, wifiPassword: e.target.value })}
                                                className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10"
                                                placeholder="WiFi password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowWifiPassword(!showWifiPassword)}
                                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                            >
                                                {showWifiPassword ? (
                                                    <EyeOff className="h-5 w-5" />
                                                ) : (
                                                    <Eye className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            WiFi Country
                                        </label>
                                        <select
                                            value={formData.wifiCountry}
                                            onChange={(e) => setFormData({ ...formData, wifiCountry: e.target.value })}
                                            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                                        >
                                            <option value="GB">GB - United Kingdom</option>
                                            <option value="US">US - United States</option>
                                            <option value="FR">FR - France</option>
                                            <option value="DE">DE - Germany</option>
                                            <option value="ES">ES - Spain</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <p className="text-sm text-blue-800">
                                    The device will be created without a serial number. 
                                    The Raspberry Pi will register its serial number automatically 
                                    when it first connects.
                                </p>
                            </div>

                            {error && (
                                <div className="p-4 bg-red-50 text-red-700 rounded-md flex items-center">
                                    <AlertTriangle className="w-5 h-5 mr-2" />
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={registering}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={registering}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                {registering ? 'Creating...' : 'Create Device'}
                            </button>
                        </div>
                    </form>
                    </div>
                ) : (
                    <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
                        <div className="mb-6">
                            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-4">
                                <Check className="w-6 h-6 text-green-600" />
                            </div>                        
                            <p className="text-gray-600 text-center">
                                Download the configuration files and copy them to your Raspberry Pi's boot partition.
                            </p>
                        </div>

                        <div className="space-y-4">  

                            {/* Download firstrun.json */}
                            <button
                                onClick={downloadConfigJson}
                                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                <Download className="w-5 h-5 mr-2" />
                                Download firstrun.json
                            </button>

                            {/* Download firstrun.sh, firstrun-service.sh, and firstrun-user-service.sh (work together) */}
                            <button
                                onClick={downloadFirstrunFiles}
                                className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                <Download className="w-5 h-5 mr-2" />
                                Download Cloud Init Files
                            </button>

                            {/* Download setup files (for user convenience) */}
                            <button
                                onClick={downloadSetupFiles}
                                className="w-full flex items-center justify-center px-4 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                            >
                                <Download className="w-5 h-5 mr-2" />
                                Download Setup Files (setup.sh, deploy.sh, update.sh)
                            </button>

                            {/* All three of the above in one go, written straight onto
                                the card - see writeAllToSdCard above. */}
                            <div>
                                <button
                                    onClick={writeAllToSdCard}
                                    disabled={!canWriteToCard || sdCardWrite.phase === 'working'}
                                    className="w-full flex items-center justify-center px-4 py-3 bg-gray-900 text-white font-medium rounded-md hover:bg-gray-800 disabled:opacity-50"
                                >
                                    {sdCardWrite.phase === 'working' ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            {sdCardWrite.label}
                                        </>
                                    ) : (
                                        <>
                                            <HardDriveDownload className="w-5 h-5 mr-2" />
                                            Write All Files to SD Card
                                        </>
                                    )}
                                </button>

                                {!canWriteToCard ? (
                                    <p className="mt-2 flex items-start text-xs text-gray-500">
                                        <Info className="w-3.5 h-3.5 mr-1.5 mt-px flex-shrink-0" />
                                        Writing straight to the SD card needs Chrome or Edge. In this
                                        browser, use the three download buttons above.
                                    </p>
                                ) : sdCardWrite.phase === 'idle' ? (
                                    <p className="mt-2 text-xs text-gray-500">
                                        Puts the files from all three buttons above straight onto the SD card,
                                        replacing any already there. When asked, pick the card's boot
                                        partition — Windows calls it <strong className="font-semibold text-gray-700">bootfs</strong>.
                                    </p>
                                ) : sdCardWrite.phase === 'problem' ? (
                                    <div className={`mt-2 flex px-4 py-3 border rounded-md ${sdCardWrite.tone === 'warning'
                                        ? 'bg-amber-50 border-amber-200 text-amber-600'
                                        : 'bg-red-50 border-red-200 text-red-600'}`}>
                                        <AlertTriangle className="w-5 h-5 mr-2.5 flex-shrink-0" />
                                        <div className="text-sm">
                                            <p className={`font-semibold ${sdCardWrite.tone === 'warning' ? 'text-amber-900' : 'text-red-800'}`}>
                                                {sdCardWrite.title}
                                            </p>
                                            <p className={`mt-1 ${sdCardWrite.tone === 'warning' ? 'text-amber-800' : 'text-red-700'}`}>
                                                {sdCardWrite.detail}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`mt-2 px-4 py-3 border rounded-md ${sdCardWrite.phase === 'written'
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-gray-50 border-gray-200'}`}>
                                        {sdCardWrite.phase === 'written' && (
                                            <>
                                                <p className="flex items-center text-sm font-semibold text-green-800">
                                                    <Check className="w-4 h-4 mr-2 text-green-600" />
                                                    All {sdCardWrite.files.length} files are on {sdCardWrite.place}
                                                </p>
                                                <p className="mt-0.5 mb-2.5 ml-6 text-xs text-green-700">
                                                    {sdCardWrite.files.filter(f => f.state === 'replaced').length} replaced,{' '}
                                                    {sdCardWrite.files.filter(f => f.state === 'new').length} new.
                                                    The card is ready for the Pi.
                                                </p>
                                            </>
                                        )}
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
                                            {sdCardWrite.files.map(file => (
                                                <div key={file.name} className="flex items-center min-w-0">
                                                    {file.state === 'waiting' && <Circle className="w-4 h-4 mr-2 flex-shrink-0 text-gray-300" />}
                                                    {file.state === 'writing' && <Loader2 className="w-4 h-4 mr-2 flex-shrink-0 text-gray-500 animate-spin" />}
                                                    {(file.state === 'replaced' || file.state === 'new') && (
                                                        <Check className="w-4 h-4 mr-2 flex-shrink-0 text-green-600" />
                                                    )}
                                                    <code className={`min-w-0 truncate text-[13px] leading-5 ${file.state === 'waiting' ? 'text-gray-400' : 'text-gray-800'}`}>
                                                        {file.name}
                                                    </code>
                                                    {(file.state === 'replaced' || file.state === 'new') && (
                                                        <span className="ml-auto pl-2 text-xs text-gray-600">{file.state}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Optional: enable SSH on this card. Empty file, handled
                                entirely in the browser - see downloadSshFile above. */}
                            <div className="border-t pt-4">
                                <button
                                    onClick={downloadSshFile}
                                    className="w-full flex items-center justify-center px-4 py-3 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                                >
                                    <Download className="w-5 h-5 mr-2" />
                                    Download ssh file (optional — enables SSH access)
                                </button>
                                <p className="mt-2 text-xs text-gray-500">
                                    Copy to the boot partition to allow remote access to this Pi.
                                    Leave it out for customer devices.
                                </p>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="font-medium text-gray-900 mb-2">Next Steps:</h4>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">                                   
                                    <li>Insert SD card and boot the Raspberry Pi</li>
                                    <li>The Pi will configure itself and register automatically</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeviceRegistrationForm;